/**
 * The stat cards above a list — list engine v2, "the list is the dashboard".
 *
 * A card is a stored definition plus the number it currently reads. The two
 * are fetched separately on purpose: the definition is a row somebody saved,
 * and the number is an answer that changes every time the data does. Keeping
 * them apart is what lets a card redraw without the strip flickering, and what
 * lets the builder's preview ask for a number for a card that does not exist
 * yet.
 *
 * The card's number is deliberately *not* narrowed by the list's current
 * filter. A card is a saved filter of its own, and clicking one applies it to
 * the list — if the card also read through whatever the list was showing, then
 * clicking card A would change what card B says, and nobody could tell which
 * number they were looking at.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CardAudience, CardDefinition, CardResult, ListCard } from '@rowted/shared'
import { cardProblem } from '@rowted/shared'
import { request as tenantRequest, type RequestOptions } from './api.js'
import type { ListArea } from './use-list.js'

export interface CardDraft {
  definition: CardDefinition
  audience: CardAudience
  orgUnitId?: string | null
}

export interface UseCardsResult {
  cards: ListCard[]
  /** The number each card reads, by card id. Absent until it arrives. */
  results: Record<string, CardResult | null>
  loading: boolean
  error: string | null
  create: (draft: CardDraft) => Promise<void>
  update: (id: string, draft: CardDraft) => Promise<void>
  remove: (id: string) => Promise<void>
  /** Drag to reorder: put `id` at `toIndex` and renumber the strip. */
  move: (id: string, toIndex: number) => Promise<void>
  /**
   * The number a definition would read, for the builder's live preview.
   *
   * The same endpoint the cards themselves use, so a preview cannot show a
   * different figure from the card it is a preview of.
   */
  preview: (definition: CardDefinition, signal?: AbortSignal) => Promise<CardResult>
  reload: () => void
}

/** The viewer's today, which is what every period is measured back from. */
const today = (): string => new Date().toISOString().slice(0, 10)

export function useCards(
  area: ListArea,
  enabled = true,
  /**
   * The fetch. Defaults to the office app's; Rowted Admin passes
   * `adminRequest` so the strip reads `/api/v1/platform/clients/cards`
   * (`docs/rowted-admin-v2.md` §2.8).
   */
  request: <T>(path: string, options?: RequestOptions) => Promise<T> = tenantRequest,
): UseCardsResult {
  const send = request
  const [cards, setCards] = useState<ListCard[]>([])
  const [results, setResults] = useState<Record<string, CardResult | null>>({})
  const [loading, setLoading] = useState(enabled)
  const [error, setError] = useState<string | null>(null)
  const [token, setToken] = useState(0)

  const reload = useCallback(() => setToken((value) => value + 1), [])

  useEffect(() => {
    if (!enabled) { setLoading(false); return }
    let cancelled = false
    setLoading(true)

    send<{ cards: ListCard[] }>(`/${area}/cards`)
      .then((payload) => {
        if (cancelled) return
        setCards(payload.cards)
        setError(null)
      })
      .catch((cause: unknown) => {
        if (cancelled) return
        // A list whose cards failed is still a list. The strip goes away and
        // the message goes with it rather than over the rows.
        setCards([])
        setError(cause instanceof Error ? cause.message : 'Cards could not be loaded.')
      })
      .finally(() => { if (!cancelled) setLoading(false) })

    return () => { cancelled = true }
  }, [area, enabled, token])

  /*
   * One request for the whole strip, fired when the strip changes.
   *
   * It was one POST per card, on the argument that a batch would let the
   * slowest card hold up the rest of the strip. Measured, that argument is
   * backwards: a card's own SQL is a few milliseconds, and nearly all of the
   * 130–150ms each one cost was the per-request work in front of it — a
   * connection out of the pool, `SET LOCAL app.tenant_id`, the session, the
   * permission decision. Eight cards paid that eight times, and with a pool
   * smaller than the strip they queued behind one another anyway, so "parallel"
   * was only ever parallel on the wire. One request pays it once.
   *
   * The cards still answer independently on the server, so one broken
   * definition still leaves the other numbers on the screen — which is the
   * part of the old behaviour worth keeping.
   */
  const asOf = useMemo(today, [token])
  const inflight = useRef<AbortController | null>(null)

  useEffect(() => {
    if (cards.length === 0) { setResults({}); return }

    inflight.current?.abort()
    const controller = new AbortController()
    inflight.current = controller

    // A card that cannot be measured is not asked about; the strip draws the
    // problem instead of a number that would be NaN.
    const asking = cards.filter((card) => !cardProblem(card.definition))
    if (asking.length === 0) { setResults({}); return }

    send<{ results: Record<string, CardResult | { error: string }> }>(
      `/${area}/aggregates`,
      {
        method: 'POST',
        body: {
          asOf,
          cards: asking.map((card) => ({ key: card.id, definition: card.definition })),
        },
        signal: controller.signal,
      })
      .then((payload) => {
        if (controller.signal.aborted) return
        const next: Record<string, CardResult | null> = {}
        for (const card of asking) {
          const answer = payload.results[card.id]
          // `error` rather than a number: that card could not be measured, and
          // null is what the strip already draws for one that could not.
          next[card.id] = answer && !('error' in answer) ? answer : null
        }
        setResults(next)
      })
      .catch(() => {
        if (controller.signal.aborted) return
        setResults({})
      })

    return () => controller.abort()
  }, [area, asOf, cards])

  const create = useCallback(async (draft: CardDraft) => {
    await send(`/${area}/cards`, { method: 'POST', body: draft })
    reload()
  }, [area, reload])

  const update = useCallback(async (id: string, draft: CardDraft) => {
    await send(`/${area}/cards/${id}`, { method: 'PUT', body: draft })
    reload()
  }, [area, reload])

  const remove = useCallback(async (id: string) => {
    await send(`/${area}/cards/${id}`, { method: 'DELETE' })
    reload()
  }, [area, reload])

  const move = useCallback(async (id: string, toIndex: number) => {
    const from = cards.findIndex((card) => card.id === id)
    if (from < 0 || from === toIndex) return

    const next = [...cards]
    const [moved] = next.splice(from, 1)
    next.splice(toIndex, 0, moved!)

    // Optimistic, because a card that lags behind the pointer feels broken.
    setCards(next.map((card, index) => ({ ...card, sortOrder: index + 1 })))

    /*
     * Only the cards whose position actually changed, and only the ones this
     * person may write. Somebody else's shared card keeps its own order —
     * dragging it locally is a rearrangement nobody else sees, which is better
     * than a 404 halfway through renumbering the strip.
     */
    const writes = next
      .map((card, index) => ({ card, index }))
      .filter(({ card, index }) => card.editable && cards[index]?.id !== card.id)
      .map(({ card, index }) => send(`/${area}/cards/${card.id}`, {
        method: 'PUT',
        body: {
          definition: card.definition,
          audience: card.audience,
          orgUnitId: card.orgUnitId,
          sortOrder: index + 1,
        },
      }))

    await Promise.all(writes)
    reload()
  }, [area, cards, reload])

  const preview = useCallback(
    (definition: CardDefinition, signal?: AbortSignal) =>
      send<CardResult>(`/${area}/aggregate`, {
        method: 'POST',
        body: { definition, asOf: today() },
        signal,
      }),
    [area])

  return { cards, results, loading, error, create, update, remove, move, preview, reload }
}
